import type { ConsentPreferences } from "@/lib/consent/consentTypes";

export const ATTRIBUTION_STORAGE_KEY = "justicedrive_attribution_v1";
export const ATTRIBUTION_TTL_DAYS = 90;

const ATTRIBUTION_TTL_MS = ATTRIBUTION_TTL_DAYS * 24 * 60 * 60 * 1000;
const INTERNAL_HOSTS = new Set(["justicedrive.co", "www.justicedrive.co"]);
const SENSITIVE_PREFIXES = [
  "/analysis/",
  "/checkout/",
  "/recover",
  "/defense-hub/",
  "/records/",
  "/mail/",
  "/admin/",
];

const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid", "msclkid"] as const;
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

export type LandingType =
  | "TBWD"
  | "TR205"
  | "MYCITATIONS"
  | "CVC_22350"
  | "CVC_22349_A"
  | "CVC_22349_B"
  | "CVC_21453_A"
  | "CVC_21461_A"
  | "HOME"
  | "GUIDE"
  | "PRODUCT"
  | "OTHER_PUBLIC";

export type AttributionTouch = {
  source: string;
  medium: string;
  campaign?: string;
  term?: string;
  content?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  msclkid?: string;
  landing_path: string;
  landing_type: LandingType;
  violation_code?: string;
  referrer_host?: string;
  channel: "paid_search" | "organic" | "referral" | "direct" | "unknown";
  captured_at: string;
};

export type AttributionState = {
  first_touch: AttributionTouch | null;
  last_touch: AttributionTouch | null;
  expires_at: string;
};

export type AttributionSnapshotPayload = {
  first_touch: AttributionTouch | null;
  last_touch: AttributionTouch | null;
};

export type CaptureAttributionInput = {
  pathname: string;
  search?: string;
  referrer?: string;
  consent: Pick<ConsentPreferences, "analytics" | "advertising"> | null;
  now?: Date;
};

const normalizeValue = (value: string | null | undefined, maxLength = 128, lower = true): string | undefined => {
  const raw = String(value ?? "").trim();
  if (!raw) return undefined;
  const normalized = (lower ? raw.toLowerCase() : raw).replace(/[^a-zA-Z0-9_.:/@+\- ]+/g, "_");
  return normalized.slice(0, maxLength) || undefined;
};

const normalizePath = (pathname: string): string => {
  const raw = String(pathname || "/").split("?")[0].split("#")[0] || "/";
  const withSlash = raw.startsWith("/") ? raw : `/${raw}`;
  return withSlash.replace(/\/+$/, "") || "/";
};

export const isSensitiveAttributionPath = (pathname: string): boolean => {
  const normalized = normalizePath(pathname).toLowerCase();
  return SENSITIVE_PREFIXES.some((prefix) => normalized === prefix.replace(/\/$/, "") || normalized.startsWith(prefix));
};

const classifyLanding = (pathname: string): { landing_type: LandingType; violation_code?: string } => {
  const path = normalizePath(pathname).toLowerCase();
  if (path === "/") return { landing_type: "HOME" };
  if (path === "/trial-by-written-declaration-california") return { landing_type: "TBWD" };
  if (path === "/tr-205-california") return { landing_type: "TR205" };
  if (path === "/mycitations-online-trial-by-declaration") return { landing_type: "MYCITATIONS" };
  if (path === "/california-vehicle-code/22350") return { landing_type: "CVC_22350", violation_code: "22350" };
  if (path === "/california-vehicle-code/22349-a") return { landing_type: "CVC_22349_A", violation_code: "22349-a" };
  if (path === "/california-vehicle-code/22349-b") return { landing_type: "CVC_22349_B", violation_code: "22349-b" };
  if (path === "/california-vehicle-code/21453-a") return { landing_type: "CVC_21453_A", violation_code: "21453-a" };
  if (path === "/california-vehicle-code/21461-a") return { landing_type: "CVC_21461_A", violation_code: "21461-a" };
  if (path === "/guides" || path.startsWith("/guides/")) return { landing_type: "GUIDE" };
  if (path.startsWith("/product/")) return { landing_type: "PRODUCT" };
  return { landing_type: "OTHER_PUBLIC" };
};

const getReferrerHost = (referrer: string | undefined): string | undefined => {
  if (!referrer) return undefined;
  try {
    return normalizeValue(new URL(referrer).hostname.replace(/^www\./, ""), 160);
  } catch {
    return undefined;
  }
};

const isInternalReferrer = (referrerHost: string | undefined, currentHost: string | undefined): boolean => {
  const host = normalizeValue(currentHost?.replace(/^www\./, ""), 160);
  if (!referrerHost || !host) return false;
  return referrerHost === host || INTERNAL_HOSTS.has(referrerHost);
};

const hasUtm = (params: URLSearchParams): boolean => UTM_KEYS.some((key) => params.has(key));

const hasPaidClickId = (params: URLSearchParams): boolean => CLICK_ID_KEYS.some((key) => params.has(key));

const classifyChannel = (
  params: URLSearchParams,
  referrerHost: string | undefined,
): Pick<AttributionTouch, "source" | "medium" | "channel"> => {
  const utmMedium = normalizeValue(params.get("utm_medium"), 64);
  if (params.get("gclid") || params.get("gbraid") || params.get("wbraid")) {
    return { source: "google", medium: utmMedium || "paid_search", channel: "paid_search" };
  }
  if (params.get("msclkid")) {
    return { source: "microsoft", medium: utmMedium || "paid_search", channel: "paid_search" };
  }
  const utmSource = normalizeValue(params.get("utm_source"), 64);
  if (utmSource || utmMedium) {
    return {
      source: utmSource || "unknown",
      medium: utmMedium || "unknown",
      channel: utmMedium === "cpc" || utmMedium === "paid_search" || utmMedium === "ppc" ? "paid_search" : "unknown",
    };
  }
  if (referrerHost?.includes("google.")) {
    return { source: "google", medium: "organic", channel: "organic" };
  }
  if (referrerHost?.includes("bing.")) {
    return { source: "bing", medium: "organic", channel: "organic" };
  }
  if (referrerHost) {
    return { source: "referral", medium: "referral", channel: "referral" };
  }
  return { source: "direct", medium: "direct", channel: "direct" };
};

const sanitizeTouchForConsent = (touch: AttributionTouch, consent: Pick<ConsentPreferences, "advertising">): AttributionTouch => {
  if (consent.advertising) return touch;
  const { gclid: _gclid, gbraid: _gbraid, wbraid: _wbraid, msclkid: _msclkid, ...withoutClickIds } = touch;
  return withoutClickIds;
};

export const buildAttributionTouch = ({
  pathname,
  search = "",
  referrer,
  consent,
  now = new Date(),
}: CaptureAttributionInput): AttributionTouch | null => {
  if (!consent || (!consent.analytics && !consent.advertising)) return null;
  const landingPath = normalizePath(pathname);
  if (isSensitiveAttributionPath(landingPath)) return null;
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const referrerHost = getReferrerHost(referrer);
  const currentHost = typeof window !== "undefined" ? window.location.hostname : undefined;
  const explicitAcquisition = hasUtm(params) || hasPaidClickId(params) || Boolean(referrerHost && !isInternalReferrer(referrerHost, currentHost));
  if (!explicitAcquisition && referrerHost && isInternalReferrer(referrerHost, currentHost)) return null;
  const landing = classifyLanding(landingPath);
  const channel = classifyChannel(params, referrerHost);
  const touch: AttributionTouch = {
    ...channel,
    campaign: normalizeValue(params.get("utm_campaign")),
    term: normalizeValue(params.get("utm_term")),
    content: normalizeValue(params.get("utm_content")),
    gclid: normalizeValue(params.get("gclid"), 256, false),
    gbraid: normalizeValue(params.get("gbraid"), 256, false),
    wbraid: normalizeValue(params.get("wbraid"), 256, false),
    msclkid: normalizeValue(params.get("msclkid"), 256, false),
    landing_path: landingPath,
    landing_type: landing.landing_type,
    violation_code: landing.violation_code,
    referrer_host: referrerHost,
    captured_at: now.toISOString(),
  };
  return sanitizeTouchForConsent(touch, consent);
};

const parseStoredState = (now = new Date()): AttributionState | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AttributionState>;
    if (!parsed.expires_at || Date.parse(parsed.expires_at) <= now.getTime()) {
      window.localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return null;
    }
    return {
      first_touch: parsed.first_touch ?? null,
      last_touch: parsed.last_touch ?? null,
      expires_at: parsed.expires_at,
    };
  } catch {
    return null;
  }
};

const isDirectTouch = (touch: AttributionTouch): boolean => touch.source === "direct" || touch.channel === "direct";

const hasKnownNonDirect = (touch: AttributionTouch | null): boolean => Boolean(touch && !isDirectTouch(touch));

export const applyAttributionTouch = (touch: AttributionTouch, now = new Date()): AttributionState | null => {
  if (typeof window === "undefined") return null;
  const current = parseStoredState(now);
  const next: AttributionState = {
    first_touch: current?.first_touch ?? touch,
    last_touch: isDirectTouch(touch) && hasKnownNonDirect(current?.last_touch ?? null) ? current?.last_touch ?? touch : touch,
    expires_at: new Date(now.getTime() + ATTRIBUTION_TTL_MS).toISOString(),
  };
  window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(next));
  return next;
};

export const clearAttributionStorage = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
};

export const getCheckoutAttributionSnapshot = (): AttributionSnapshotPayload | null => {
  const state = parseStoredState();
  if (!state || (!state.first_touch && !state.last_touch)) return null;
  return {
    first_touch: state.first_touch,
    last_touch: state.last_touch,
  };
};
